import type { ApiPriceData } from '../../../../../../models/ecommerce/product/ApiProduct';
import type { PriceData } from '../../../priceData/priceData';
import type { PromotionMaxUsage } from './maxUsage';

export const CampaignPromoType = {
    FIXED_PRICE: 'FIXED_PRICE',
    MULTIBUY: 'MULTIBUY',
    PERCENTAGE: 'PERCENTAGE',
} as const;

export const KNOWN_CAMPAIGN_PROMO_TYPES = [
    CampaignPromoType.FIXED_PRICE,
    CampaignPromoType.MULTIBUY,
    CampaignPromoType.PERCENTAGE,
] as const;

export type CampaignEcomPromotionType = (typeof KNOWN_CAMPAIGN_PROMO_TYPES)[number];

/** Everyday Low Price — not a campaign type. */
export const EDLP_PROMO_TYPE = 'EDLP' as const;

export type EdlpEcomPromotionType = typeof EDLP_PROMO_TYPE;

export type EcomPromotionType = CampaignEcomPromotionType | EdlpEcomPromotionType;

export const isKnownCampaignPromoType = (
    type: string | undefined,
): type is CampaignEcomPromotionType => !!type && KNOWN_CAMPAIGN_PROMO_TYPES.includes(type);

/** Domain model for ecommerce promotion. Mapped from HybrisPromotion when doing direct calls OR the representation of local API entity to which we map Hybris models on backend.     */
export type EcomPromotion = CampaignEcomPromotion | EdlpEcomPromotion;

export type CampaignEcomPromotion =
    | FixedEcomPromotion
    | MultiBuyEcomPromotion
    | PercentageEcomPromotion;

export interface BaseEcomPromotion {
    identifier: string;
    description?: string;
    productId: string;
    variantId?: string;
    isMemberPrice: boolean;
    /**
     * @deprecated Will be removed in favor of maxUse object when product data also use maxUse
     */
    maxUseText?: string;
    maxUse?: PromotionMaxUsage;
    comparativePriceData?: ApiPriceData;
    higherThanHistoricalPrice?: boolean;
    /**
     * ISO-8601 start of the promotional period. Only present for promos sourced
     * from the assortment API (`onlinePromotions` on the Loop54 product feed).
     * Cart / order promos coming from Hybris do not carry these fields.
     */
    startDate?: string;
    /** ISO-8601 end of the promotional period. See {@link startDate}. */
    endDate?: string;
}

export interface PercentageEcomPromotion extends BaseEcomPromotion {
    type: typeof CampaignPromoType.PERCENTAGE;
    percentageDiscount: number;
    adjustedPercentageDiscount?: number;
}

export interface MultiBuyEcomPromotion extends BaseEcomPromotion {
    type: typeof CampaignPromoType.MULTIBUY;
    numberOfProductRequired: number;
    bundlePrice: PriceData;
}

export interface FixedEcomPromotion extends BaseEcomPromotion {
    type: typeof CampaignPromoType.FIXED_PRICE;

    /**
     * Price per piece when unit is "st" OR price per kilo if unit is "kg".
     */
    fixedPrice: PriceData;

    /**
     * Price per piece, no matter the unit.
     */
    fixedPiecePrice?: PriceData;
    unit?: 'st' | 'kg';
}

/**
 * Everyday Low Price — permanent price level (not a temporary campaign).
 */
export interface EdlpEcomPromotion extends BaseEcomPromotion {
    type: EdlpEcomPromotionType;

    /** Bundle price for {@link numberOfProductRequired} products (same role as multi-buy). */
    bundlePrice: PriceData;

    /**
     * Price per piece from the promo payload (optional; secondary UI uses product regular price).
     */
    piecePrice?: PriceData;
    unit?: 'st' | 'kg';

    /** Number of products the {@link bundlePrice} applies to (“X för Y”). Required to map EDLP. */
    numberOfProductRequired: number;
}
